// Step 4: the one entry for both arms of the A/B test (docs/30; Figma 21 /
// 21.13 / 22 / 23).
//
// "Here's exactly what casting sees" is the promise this page makes, so the card
// renders the real materials rather than a summary of them.
//
// Who still owes a plan goes through the membership paywall. When depends on
// the arm, which the server keeps per account (docs/30 #19):
//   test A  this review first. Continue goes to the paywall; back from the
//           purchase (paid=1), the payment sends the submission: a spinner,
//           then the sent page, or the outcome right here when it can't go.
//   test B  the paywall first, so this page sends them straight on to it.
//           Back from the purchase: this review, and Send.
// Who owes nothing (US talent, an existing Pro, an active trial, a free casting
// call) gets the review and Send in either arm.

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionSheet from "../common/SubmissionSheet";
import SubmissionOutcome, { OutcomePlan } from "../common/SubmissionOutcome";
import RepresentationSheet from "../common/RepresentationSheet";
import { canSend, needsPaywall } from "../../../models/submissionStep4Model";
import { paysFirst, paysLast, resolveStep4Variant, skipsPaywall } from "../common/step4Variant";
import {
  consumeLeftForPaywall,
  isPaidReturn,
  CHECKOUT_PARAM,
  markLeftForPaywall,
  PAID_PARAM,
} from "../common/membershipPaywall";
import { goToDashboard } from "../common/dashboard";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { parseSubmissionParams, withParam } from "../common/submissionParams";
import { projectLine } from "../common/projectLine";
import { alreadySubmittedPath, rolePickerPath, stopsMidway } from "../../../models/submissionFlowModel";
import {
  IRepresentationDto,
  ISendSubmissionResponse,
  ISetRepresentationRequest,
  ISubmissionMembershipDto,
  getSubmissionFlowApi,
  Step4Variant,
} from "../../../shared/api/submission";
import { trackCheckoutCompleted, trackSubmissionEvent } from "../analytics";
import { membershipPrimary, membershipSecondary } from "../common/membershipButton";
import {
  COUNTRY_NOT_ELIGIBLE,
  leaveForCastingCalls,
  leaveForDashboard,
  trackCountryClosedShown,
} from "../common/countryClosed";
import SignedInAs from "../common/SignedInAs";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

// "Representation: Nicolas Belik" / "Self-represented" (Figma 23 / 21.13).
//
// Self-represented is the default and also the fallback: an account that has
// never been asked, and one whose representation arrives without a usable name,
// both read as self-represented rather than as an empty "Representation:".
const representationLabel = (rep?: IRepresentationDto): string => {
  const name = rep ? rep.agentName || rep.agencyName || "" : "";
  if (!rep || rep.type === "selfRepresented" || !name) {
    return i18next.t("Self-represented");
  }
  if (rep.type === "invited") {
    return i18next.t("Representation: {{name}} (invited)", { name });
  }
  return i18next.t("Representation: {{name}}", { name });
};

// A pencil, drawn inline rather than as a glyph: the text "Edit" competed with
// the OPEN CALL label for the same corner, and Figma puts an icon there.
const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M10.9 2.6l2.5 2.5-7.9 7.9-3.1.6.6-3.1 7.9-7.9z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M9.6 3.9l2.5 2.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

// True when the row is showing the default, which is what decides whether the
// action beside it reads "I have representation" or "Change".
const isSelfRepresented = (rep?: IRepresentationDto): boolean =>
  representationLabel(rep) === i18next.t("Self-represented");

// Where the page is. Only "review" shows the card's content; until the arm is
// known it keeps its skeleton, so test B never flashes a review on its way to
// the paywall.
//   loading     the review, the membership answer and the arm are on their way
//   review      the card, with Send, or Continue to the paywall (test A)
//   leaving     on the way to the paywall
//   confirming  back from a purchase, waiting for the plan to reach the account
//   slow        the plan hasn't reached it in time
//   sending     test A: the payment is sending the submission
//   sendFailed, closedAfterPayment, countryAfterPayment  test A, when it couldn't go
type Phase =
  | "loading"
  | "review"
  | "leaving"
  | "confirming"
  | "slow"
  | "sending"
  | "sendFailed"
  | "closedAfterPayment"
  | "countryAfterPayment";

// Codes that mean the casting call is gone, not that the send failed: the plan
// stays, and the way on is another casting call (docs/30 §3).
const CLOSED_CODES = ["breakdownClosed", "breakdownNotFound", "roleCast"];

const planOf = (membership: ISubmissionMembershipDto | null): OutcomePlan =>
  membership && (membership.membershipStatus === "trial" || membership.membershipStatus === "pro")
    ? membership.membershipStatus
    : null;

const SubmissionReviewPage: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  roleId,
  sessionId,
  draft,
  review,
  submissionInfo,
  membership,
  loading,
  error,
  signupErrorCode,
  ensureDraft,
  loadReview,
  saveRepresentation,
  setJobAlerts,
  sendSubmission,
  sendAfterPayment,
  holdSubmission,
  recordExposure,
  startMembershipCheckout,
  startDirectCheckout,
  waitForPlan,
  refreshGate,
  setStep4Error,
}) => {
  const [showRepresentation, setShowRepresentation] = useState(false);
  const [sending, setSending] = useState(false);
  const [phase, setPhase] = useState<Phase>("loading");
  // Once the review has been on screen, leaving for the paywall keeps it there.
  const [reviewSeen, setReviewSeen] = useState(false);
  // The page's own pick until the server answers with the account's arm.
  const [arm, setArm] = useState<Step4Variant>(() => resolveStep4Variant(location.search));
  const submissionId = draft ? draft.submissionId : 0;
  const params = parseSubmissionParams(location.search);
  const paid = isPaidReturn(location.search);
  // Off the URL once the flow moves on: paid=1 means "just back from paying".
  const flowSearch = withParam(withParam(location.search, PAID_PARAM, null), CHECKOUT_PARAM, null);
  // Back without paying, read once and then dropped from the URL:
  //   checkout=cancelled  tests C and D, Stripe's back link (recorded as LITE here);
  //   checkout=declined   tests A and B, the membership paywall's Lite or close, forwarded
  //                       by the saved page (held here, which records LITE).
  const [cancelledCheckout] = useState(
    () => new URLSearchParams(location.search).get(CHECKOUT_PARAM) === "cancelled"
  );
  const [declinedOnPaywall] = useState(
    () => new URLSearchParams(location.search).get(CHECKOUT_PARAM) === "declined"
  );

  // Leaves for the plan screen: the membership paywall (another domain), or in
  // the mock its stand-in. The checkout is recorded on the way; only a casting
  // call that's gone, or a submission already sent, keeps the actor here.
  const goToPaywall = async (id: number, currentArm: Step4Variant) => {
    setPhase("leaving");
    // Tests C and D go straight to Stripe; the paywall only when that can't start.
    const direct = skipsPaywall(currentArm);
    trackSubmissionEvent(direct ? "sf_step4_to_checkout" : "sf_step4_to_paywall", { variant: currentArm });
    const { url, blocked } = direct
      ? await startDirectCheckout({ submissionId: id, search: location.search, variant: currentArm })
      : await startMembershipCheckout({ submissionId: id, search: location.search });
    if (url) {
      markLeftForPaywall(id);
      if (/^(https?:)?\/\//i.test(url)) window.location.assign(url);
      else history.push(url);
      return;
    }
    if (blocked === "alreadySubmitted" && (breakdownId || params.bd)) {
      history.replace(alreadySubmittedPath(flowSearch, (breakdownId || params.bd) as string));
      return;
    }
    if (blocked === "alreadySent") {
      history.replace(`/submission/sent${flowSearch}`);
      return;
    }
    // A new signup we can't place picks a film center before paying: step 2's card
    // (the effect below takes them there; the error is the model's).
    if (blocked === "homeLocationRequired") return;
    // Closed since the page loaded: the gate takes them to the closed page; a
    // role that isn't the breakdown's, to pick another.
    const info = await refreshGate({ history, search: location.search });
    if (info && !stopsMidway(info, roleId || params.roleid)) setPhase("review");
  };

  // Test A: the payment sends the submission (docs/30 §3). On a failure the
  // actor can try again from here; the server's background job tries once more
  // if they leave instead.
  const autoSend = async (id: number) => {
    setPhase("sending");
    const result = await sendAfterPayment(id);
    if (result.sent) {
      trackSubmissionEvent("sf_step4_sent", { variant: arm, afterPayment: true });
      history.replace(`/submission/sent${flowSearch}`);
      return;
    }
    trackSubmissionEvent("sf_step4_send_after_payment_failed", { code: result.error });
    if (result.error === "alreadySubmitted" && (breakdownId || params.bd)) {
      history.replace(alreadySubmittedPath(flowSearch, (breakdownId || params.bd) as string));
      return;
    }
    if (result.error === "roleNotFound" && (breakdownId || params.bd)) {
      history.replace(rolePickerPath(flowSearch, (breakdownId || params.bd) as string, roleId || params.roleid));
      return;
    }
    // Casting doesn't take their country: trying again can't help, so the exits (countryClosed.ts).
    if (result.error === COUNTRY_NOT_ELIGIBLE) {
      trackCountryClosedShown("review");
      setPhase("countryAfterPayment");
      return;
    }
    setPhase(CLOSED_CODES.indexOf(result.error || "") >= 0 ? "closedAfterPayment" : "sendFailed");
  };

  // Once the membership answer is in. The arm is asked only of an actor who
  // still owes a plan, or who just bought one (to know whether the payment
  // sends): the server answers the arm stored on the account, which wins.
  const route = async (id: number, answer: ISubmissionMembershipDto | null, backFromPaywall = false) => {
    const owes = needsPaywall(answer);
    if (!owes && !paid) {
      setPhase("review");
      return;
    }
    const assigned: Step4Variant = await recordExposure({
      variant: arm,
      sessionId,
      breakdownId: breakdownId || params.bd || null,
      roleId: roleId || params.roleid || null,
    });
    setArm(assigned);
    // Test B goes straight on to the plan, except for an actor who just came
    // back from it without paying: they get the review, and Continue.
    if (owes && paysFirst(assigned) && !backFromPaywall) {
      await goToPaywall(id, assigned);
      return;
    }
    if (paid && !owes && paysLast(assigned)) {
      await autoSend(id);
      return;
    }
    setPhase("review");
  };

  // Back from a purchase, the plan reaches the account seconds after the
  // payment (Stripe's webhook, then the membership tables).
  const confirmPlan = async (id: number) => {
    setPhase("confirming");
    const answer = await waitForPlan();
    if (!answer) {
      trackSubmissionEvent("sf_step4_plan_slow", { variant: arm });
      setPhase("slow");
      return;
    }
    trackCheckoutCompleted(breakdownId || params.bd, { variant: arm });
    await route(id, answer);
  };

  useEffect(() => {
    const boot = async () => {
      if (!hasFlowContext({ search: location.search, breakdownId })) {
        history.replace(fallbackTo(location.search));
        return;
      }
      if (!isSubmissionAuthed()) {
        history.replace(`/submission/flow/verify-email${location.search}`);
        return;
      }
      trackSubmissionEvent("sf_step4_review_viewed", { variant: arm, paid });

      // The breakdown is re-checked on every step-4 entry point. A closed one
      // redirects out of here to the closed page, a role that isn't the
      // breakdown's to pick another, before anything is offered: there is no
      // point loading a submission nobody can send.
      const info = await refreshGate({ history, search: location.search, paid });
      if (!info || stopsMidway(info, roleId || params.roleid)) return;

      // A refresh empties the store, and the submission id lives in it — so
      // without this the card sat on its skeleton forever and Send stayed
      // disabled, on a screen whose whole job is showing what was made. The
      // draft comes back from the server rather than from anything remembered
      // client-side: createDraft is idempotent per breakdown (step 2 already
      // relies on that), so this returns the existing submission.
      let current = draft;
      if (!current || !current.submissionId) {
        current = await ensureDraft({
          breakdownId: breakdownId || params.bd || null,
          roleId: roleId || params.roleid || null,
        });
      }
      if (!current || !current.submissionId) return;

      const loaded: any = await loadReview(current.submissionId);
      if (!loaded) {
        setPhase("review");
        return;
      }
      // Already gone. Offering Send again would invite a second submission for
      // a role that has one, and the visitor's own history is the reason they
      // are here — a refresh on the success page lands them back through here.
      if (loaded.status === "Sent") {
        history.replace(`/submission/sent${flowSearch}`);
        return;
      }

      // Read either way, so a mark from this trip never outlives it.
      // Stripe's back link (checkout=cancelled) is a way back too, even when this tab
      // lost its own mark: test D must not send them straight back to Stripe.
      const backFromPaywall =
        (consumeLeftForPaywall(current.submissionId) || cancelledCheckout || declinedOnPaywall) && !paid;
      if (cancelledCheckout && !paid) {
        trackSubmissionEvent("sf_step4_checkout_cancelled", { variant: arm });
        getSubmissionFlowApi()
          .recordPlanDeclined(current.submissionId)
          .catch(() => undefined);
        history.replace(`${location.pathname}${flowSearch}`);
      }
      // Tests A and B, declined on the paywall: held as the saved page used to hold it
      // (Saved, until the deadline, the reminder on; reason ignoredTrial records the LITE),
      // so an actor who just closes the tab here is where they were before this screen.
      if (declinedOnPaywall && !paid) {
        trackSubmissionEvent("sf_step4_paywall_declined", { variant: arm });
        getSubmissionFlowApi()
          .holdSubmission(current.submissionId, { reason: "ignoredTrial", emailReminder: true })
          .catch(() => undefined);
        history.replace(`${location.pathname}${flowSearch}`);
      }
      if (backFromPaywall) trackSubmissionEvent("sf_step4_back_from_paywall", { variant: arm });

      if (paid && !canSend(loaded.membership)) {
        await confirmPlan(current.submissionId);
        return;
      }
      // Back from the membership paywall's checkout (tests A and B) with the plan already on the
      // account: the checkout's completion, as the flow's own checkout return says it (C and D).
      if (paid) trackCheckoutCompleted(breakdownId || params.bd, { variant: arm });
      await route(current.submissionId, loaded.membership, backFromPaywall);
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "review") setReviewSeen(true);
  }, [phase]);

  // Back from the other site, the browser may restore this page as it was left:
  // on its way out, buttons disabled. Show the review again instead.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (submissionId) consumeLeftForPaywall(submissionId);
      setSending(false);
      setPhase((p) => (p === "leaving" || p === "loading" ? "review" : p));
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [submissionId]);

  // Send refused because casting doesn't take their country (step 2 stops this first; a home
  // changed since, in another tab, ends here): no Send, the same two exits as step 2.
  const countryRefused = !!error && error.code === COUNTRY_NOT_ELIGIBLE;
  useEffect(() => {
    if (countryRefused) trackCountryClosedShown("review");
  }, [countryRefused]);

  const entitled = canSend(membership);
  const mustPay = needsPaywall(membership);
  // Back without paying (decided 2026-10-28): from Stripe in tests C and D, or declined on the
  // paywall in tests A and B. A notice, "Try again" (the arm's next step again: Stripe, or the
  // paywall) and "Continue to theWorkbook" (held, then the dashboard). The trial is named only
  // while one is open to the account; "nothing was charged" only after Stripe's page.
  const backFromStripe = cancelledCheckout && !paid && mustPay;
  const backWithoutPaying = (cancelledCheckout || declinedOnPaywall) && !paid && mustPay;
  const trialOpen = !!membership && membership.reason === "trialAvailable";
  const backNotice = backFromStripe
    ? trialOpen
      ? i18next.t("Your free PRO trial didn't start. Nothing was charged.")
      : i18next.t("Your checkout wasn't completed. Nothing was charged.")
    : trialOpen
    ? i18next.t("Your free PRO trial didn't start.")
    : i18next.t("You haven't started a PRO plan yet.");
  const hasPhoto = !!(review && review.photo);
  // The resume-or-note rule (step 2). A draft from before it, or one whose resume has since left
  // the profile, goes back to step 2 instead of on to the paywall: the server refuses to send it.
  const hasResumeOrNote = !!(review && (review.resume || (review.noteToCastingDirector || "").trim()));
  const showCard = phase === "review" || phase === "leaving";
  const needsResumeOrNote = !!review && showCard && hasPhoto && !hasResumeOrNote;

  // The job-alerts box is ticked by default (decided 2026-10-28): a new signup who hasn't
  // chosen yet is subscribed as the box first shows, behind the page, the same as a tick.
  // Unticked once (declined), it stays unticked and nothing subscribes them again.
  const alertsDefaulted = useRef(false);
  useEffect(() => {
    const alerts = review && review.jobAlerts;
    if (!showCard || !alerts || !alerts.offered || alerts.subscribed || alerts.declined) return;
    if (alertsDefaulted.current) return;
    alertsDefaulted.current = true;
    trackSubmissionEvent("sf_step4_job_alerts", { subscribe: true, byDefault: true });
    setJobAlerts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review, showCard]);

  // Already with casting for this breakdown, any role (the send, the hold, or
  // the draft after a refresh said so): the flow stops on the closed page.
  useEffect(() => {
    const code = (error && error.code) || signupErrorCode;
    if (code === "alreadySubmitted" && (breakdownId || params.bd)) {
      history.replace(alreadySubmittedPath(flowSearch, (breakdownId || params.bd) as string));
    } else if (code === "homeLocationRequired") {
      // The checkout or the send: a new signup we can't place hasn't picked a film center.
      // Step 2's card asks. Cleared on the way, or the review would send them back again
      // when they return with one.
      setStep4Error(null);
      history.replace(`/submission/flow/step/2${flowSearch}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error && error.code, signupErrorCode]);

  const onEditMaterials = () => {
    trackSubmissionEvent("sf_step4_back_to_edit", { variant: arm });
    history.push(`/submission/flow/step/2${flowSearch}`);
  };

  const onSaveForLater = async () => {
    if (!submissionId) return;
    const held = await holdSubmission({
      submissionId,
      request: { reason: "savedFromReview", emailReminder: true },
    });
    if (held) {
      trackSubmissionEvent("sf_step4_saved_for_later", { variant: arm });
      history.push(`/submission/saved${withParam(flowSearch, "from", "review")}`);
    }
  };

  // Back without paying: the way out. Held as "Save for now" holds it (the reminder emails keep
  // their promise), then theWorkbook's dashboard in one step instead of the saved page first.
  const onContinueToWorkbook = async () => {
    if (!submissionId) return;
    trackSubmissionEvent("sf_step4_cancelled_to_workbook", { variant: arm });
    await holdSubmission({
      submissionId,
      request: { reason: "savedFromReview", emailReminder: true },
    });
    goToDashboard(history);
  };

  const onPrimary = async () => {
    if (!submissionId || sending || phase !== "review") return;

    // Not entitled yet: the paywall is the next screen, not an error. Test A
    // reaches it from here; test B was sent on to it before seeing this.
    if (mustPay) {
      await goToPaywall(submissionId, arm);
      return;
    }

    setSending(true);
    const resp: any = await sendSubmission(submissionId);
    setSending(false);
    if (resp) {
      // A replay of an already-sent submission counts as success — the thing
      // the visitor asked for has happened.
      trackSubmissionEvent("sf_step4_sent", {
        variant: arm,
        replay: !!resp.alreadySent,
      });
      history.replace(`/submission/sent${flowSearch}`);
    }
  };

  const onSaveRepresentation = async (request: ISetRepresentationRequest) => {
    const saved = await saveRepresentation({ submissionId, request });
    if (saved.representation) {
      trackSubmissionEvent("sf_step4_representation_set", { type: request.type });
      setShowRepresentation(false);
    }
    return saved.error;
  };

  const onSeeCastingCalls = () => {
    trackSubmissionEvent("sf_step4_explore_more", { from: phase });
    history.push("/submission/casting-calls");
  };

  const onDashboard = () => {
    trackSubmissionEvent("sf_step4_to_dashboard", { from: phase });
    goToDashboard(history);
  };

  // Test A after the payment, when the submission couldn't go: the outcome in
  // place, with the plan line, because the plan did start.
  if (phase === "sendFailed" || phase === "closedAfterPayment" || phase === "countryAfterPayment") {
    const countryClosedNow = phase === "countryAfterPayment";
    return (
      <SubmissionLayout step={4}>
        <SubmissionOutcome
          kind={phase}
          plan={planOf(membership)}
          trialEndsUtc={membership ? membership.planEndsUtc : null}
          breakdownName={review ? review.breakdownName : undefined}
          onDashboard={countryClosedNow ? () => leaveForDashboard(history, "review") : onDashboard}
          onExplore={onSeeCastingCalls}
          onSeeCastingCalls={
            countryClosedNow ? () => leaveForCastingCalls(history, null, "review") : onSeeCastingCalls
          }
          // No Pro offer after a payment: the offer is for who submitted free.
          onGoPro={() => undefined}
          onRetry={() => autoSend(submissionId)}
        />
      </SubmissionLayout>
    );
  }

  // The review's title belongs to the arm that shows it. Back from a purchase
  // (test A sends, test B reviews) and on test B's way to the paywall, the page
  // waits with a spinner until it knows, rather than flashing the review.
  const waitingForArm =
    !reviewSeen && (phase === "loading" || phase === "leaving") && (paid || paysFirst(arm));

  if (phase === "confirming" || phase === "sending" || phase === "slow" || waitingForArm) {
    return (
      <SubmissionLayout step={4}>
        <Waiting aria-busy={phase !== "slow"} aria-live="polite">
          <Spinner aria-hidden="true" stopped={phase === "slow"} />
          <WaitingMessage>
            {phase === "sending"
              ? i18next.t("Sending your submission…")
              : phase === "confirming"
              ? i18next.t("Confirming your payment…")
              : phase === "slow"
              ? i18next.t("Payment received. Your membership is still activating.")
              : paid
              ? i18next.t("Processing…")
              : i18next.t("One moment…")}
          </WaitingMessage>
          {phase === "slow" && (
            <>
              {/* Test A's background job sends it once the plan is active;
                  test B's actor sends it themselves, from the review. */}
              {paysLast(arm) && (
                <WaitingSub>
                  {i18next.t("You can carry on — we'll finish this in the background.")}
                </WaitingSub>
              )}
              <WaitingButton type="button" onClick={() => confirmPlan(submissionId)}>
                {i18next.t("Check again")}
              </WaitingButton>
              <TextButton type="button" onClick={onDashboard}>
                {i18next.t("Go to my dashboard")}
              </TextButton>
            </>
          )}
        </Waiting>
      </SubmissionLayout>
    );
  }

  const renderCard = () => {
    if (!review || !showCard) {
      // The same outline as the loaded card, so it does not jump when the
      // review lands.
      return (
        <Card aria-busy="true">
          <CardTop>
            <Skeleton height={12} width={64} />
          </CardTop>
          <Identity>
            <Skeleton height={110} width={88} />
            <IdentityText>
              <Skeleton height={20} width={150} />
              <Skeleton height={14} width={120} />
              <Skeleton height={12} width={170} />
            </IdentityText>
          </Identity>
        </Card>
      );
    }
    return (
      <Card>
        <CardTop>
          <OpenCallBadge>{i18next.t("OPEN CALL")}</OpenCallBadge>
          <EditLink
            type="button"
            onClick={onEditMaterials}
            aria-label={i18next.t("Edit your materials")}
            title={i18next.t("Edit your materials")}
          >
            <PencilIcon />
          </EditLink>
        </CardTop>

        {/* Figma 21: the photo beside the name, not above it. Stacked, the
            card pushed Send below the fold on a 390x844 phone — the one screen
            where the button that finishes the whole flow must be in reach. */}
        <Identity>
          {review.photo ? (
            <Headshot
              src={review.photo.url || undefined}
              alt={i18next.t("Your headshot")}
            />
          ) : (
            <HeadshotMissing>{i18next.t("No photo")}</HeadshotMissing>
          )}
          <IdentityText>
            <ActorName>{review.actorName}</ActorName>
            {review.roleName && <RoleName>{review.roleName}</RoleName>}
            <ProjectLine>{projectLine(review.breakdownName, review.projectType)}</ProjectLine>
            {review.statsLine && <StatsLine>{review.statsLine}</StatsLine>}
          </IdentityText>
        </Identity>

        {/* A link out, not an expander: the breakdown is a page in its own
            right, and opening it in place would take someone off a screen they
            are in the middle of confirming. rel=noopener because target=_blank
            otherwise hands the new tab a live handle on this one. */}
        {review.breakdownDetailsUrl && (
          <DetailsLink
            href={review.breakdownDetailsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackSubmissionEvent("sf_step4_breakdown_details", { variant: arm })}
          >
            {i18next.t("View breakdown details")}
            <ExternalIcon aria-hidden="true">{"↗"}</ExternalIcon>
          </DetailsLink>
        )}

        {review.resume && <Chip>{review.resume.fileName}</Chip>}

        {/* The note is part of what casting sees, so its absence is worth
            showing too — an empty gap reads as a rendering bug, and someone who
            meant to write a note deserves to notice they did not. */}
        {review.noteToCastingDirector ? (
          <Note>{`“${review.noteToCastingDirector}”`}</Note>
        ) : (
          <NoteEmpty>{i18next.t("(No notes)")}</NoteEmpty>
        )}

        <Representation>{representationLabel(review.representation)}</Representation>
      </Card>
    );
  };

  const busy = !review || !showCard || sending || phase === "leaving";

  return (
    <SubmissionLayout step={4}>
      {showRepresentation && (
        <RepresentationSheet
          current={review ? review.representation : undefined}
          onCancel={() => setShowRepresentation(false)}
          onSave={onSaveRepresentation}
        />
      )}

      {/* An entitled visitor who has already been told why — e.g. a trial that
          started one screen ago — gets no interruption here. */}
      {error && error.code === "membershipRequired" && (
        <SubmissionSheet
          title={i18next.t("Only PRO members or Trial users can submit to roles")}
          onDismiss={() => goToPaywall(submissionId, arm)}
        >
          <SheetText>
            {i18next.t("Start your free trial and this submission goes out today.")}
          </SheetText>
        </SubmissionSheet>
      )}

      <CenterWrap>
        <Title>{i18next.t("This is your submission")}</Title>
        <SubTitle>{i18next.t("Here's exactly what casting sees.")}</SubTitle>
      </CenterWrap>

      {renderCard()}

      {/* Under the card, where Figma 21 has it. Beside the label inside the
          card, a full-width button squeezed "Self-represented" into a
          two-line sliver at the left edge. */}
      {review && showCard && (
        <RepresentationLink type="button" onClick={() => setShowRepresentation(true)}>
          {isSelfRepresented(review.representation)
            ? i18next.t("I have representation")
            : i18next.t("Change representation")}
        </RepresentationLink>
      )}

      {/* New signups only (existing members keep their own settings), ticked by default, and
          saved behind the page: the plan, the paywall and the send don't wait for it. */}
      {review && showCard && review.jobAlerts && review.jobAlerts.offered && (
        <AlertsRow>
          <AlertsCheckbox
            type="checkbox"
            id="job-alerts"
            checked={review.jobAlerts.subscribed || !review.jobAlerts.declined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              trackSubmissionEvent("sf_step4_job_alerts", { subscribe: e.target.checked });
              setJobAlerts(e.target.checked);
            }}
          />
          <AlertsLabel htmlFor="job-alerts">{i18next.t("Subscribe to receive more breakdowns by email")}</AlertsLabel>
        </AlertsRow>
      )}

      {error && error.code !== "membershipRequired" && error.code !== "homeLocationRequired" && (
        <ErrorText role="alert">
          {error.code === "materialsRequired"
            ? i18next.t("Add a resume or a note to casting before you continue.")
            : error.code === "countryNotEligible"
            ? i18next.t("Casting for this role isn't open to your country.")
            : error.message || i18next.t("Something went wrong. Please try again.")}
        </ErrorText>
      )}

      {backWithoutPaying && showCard && <CancelledNotice role="status">{backNotice}</CancelledNotice>}

      {/* Past the deadline but still open (isLate): said once more right where
          they decide, without the margin's end. */}

      {submissionInfo && submissionInfo.isLate && showCard && (
        <LateNotice>
          <b>{i18next.t("The deadline has passed.")}</b> {i18next.t("Casting is still accepting submissions, but once they choose someone it closes. Send it now.")}
        </LateNotice>
      )}

      {countryRefused ? (
        <>
          {/* The review knows no country: the list the API picks from the IP. */}
          <PrimaryButton onClick={() => leaveForCastingCalls(history, null, "review")}>
            {i18next.t("See open casting calls")}
          </PrimaryButton>
          <SecondaryButton onClick={() => leaveForDashboard(history, "review")}>
            {i18next.t("Go to my dashboard")}
          </SecondaryButton>
        </>
      ) : needsResumeOrNote ? (
        <>
          <HintText>{i18next.t("Casting needs a resume or a few words")}</HintText>
          <PrimaryButton onClick={onEditMaterials}>{i18next.t("Add a resume or a note")}</PrimaryButton>
        </>
      ) : (
      <PrimaryButton
        onClick={onPrimary}
        disabled={busy || loading || (!mustPay && !hasPhoto)}
      >
        {/* The label is the membership answer, so it must not be guessed
            while the answer is still in flight: "Send this submission"
            flipping to "Continue" a second later is the kind of shift that
            makes people distrust the button they are about to press. */}
        {busy
          ? i18next.t("One moment…")
          : backWithoutPaying
          ? i18next.t("Try again")
          : mustPay
          ? i18next.t("Continue")
          : i18next.t("Send this submission")}
      </PrimaryButton>
      )}

      {/* Saving never depends on entitlement — someone who cannot send today
          must still be able to keep what they made. */}
      {countryRefused ? null : backWithoutPaying ? (
        <TextButton type="button" onClick={onContinueToWorkbook} disabled={loading || !showCard}>
          {i18next.t("Continue to theWorkbook")}
        </TextButton>
      ) : (
        <TextButton type="button" onClick={onSaveForLater} disabled={loading || !showCard}>
          {i18next.t("Save for now")}
        </TextButton>
      )}

      {!hasPhoto && review && showCard && (
        <HintText>
          {i18next.t(
            "Every submission needs a photo. It doesn't have to be a professional headshot. It has to look like you."
          )}
        </HintText>
      )}

      {showCard && review && review.freeCastingCall ? (
        <HintText>{i18next.t("Submitting to this casting call is free")}</HintText>
      ) : showCard && entitled && membership && membership.freeMarket ? (
        <HintText>{i18next.t("Submitting is free for US talent")}</HintText>
      ) : null}

      <SignedInAs history={history} search={location.search} step={4} />
    </SubmissionLayout>
  );
};

// The box is centred on the label's first line: one line is exactly the box's height, so a
// label that wraps (French) keeps the box beside its first line.
const AlertsRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin: 4px 0 16px;
`;

const AlertsCheckbox = styled.input`
  margin: 0 10px 0 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const AlertsLabel = styled.label`
  font-size: 13px;
  color: #53627c;
  line-height: 18px;
  cursor: pointer;
`;

const CenterWrap = styled.div`
  text-align: center;
  margin: 18px 0 14px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 4px;
`;

const SubTitle = styled.div`
  font-size: 14px;
  color: #53627c;
`;

const Card = styled.div`
  border: 1px solid #d9dee6;
  border-radius: 10px;
  padding: 14px 16px 16px;
  margin-bottom: 12px;
  text-align: left;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  margin-bottom: 8px;
`;

// Sits opposite the OPEN CALL label, where Figma puts the pencil: the way back
// to step 2 from the thing you are looking at. 32px square so the icon keeps a
// finger-sized target without growing the row.
const EditLink = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: -6px -8px -6px 0;
  background: none;
  border: none;
  border-radius: 6px;
  color: #8a94a6;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #0584d7;
    background: #f2f6fb;
  }
`;

// A quiet status label, not a badge. It says what kind of call this is; the
// thing that deserves the eye on this screen is the photo and the Send button.
const OpenCallBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a6aebc;
`;

const Identity = styled.div`
  display: flex;
  align-items: flex-start;
`;

const IdentityText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 12px;
`;

// 4:5, the ratio the editor exports — the card shows the crop, not the original.
const Headshot = styled.img`
  flex: none;
  width: 88px;
  height: 110px;
  object-fit: cover;
  border-radius: 8px;
  background: #f2f4f7;
`;

const HeadshotMissing = styled.div`
  flex: none;
  width: 88px;
  height: 110px;
  border-radius: 8px;
  background: #f2f4f7;
  color: #8a94a6;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 8px;
`;

const ActorName = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #0a1f44;
  overflow-wrap: anywhere;
`;

const RoleName = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 19px;
  color: #0a1f44;
  margin-top: 4px;
`;

const ProjectLine = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: #6b7486;
  margin-top: 2px;
`;

const StatsLine = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #6b7486;
  margin-top: 2px;
`;

const DetailsLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  padding: 4px 0;
  min-height: 32px;
  color: #0584d7;
  font-size: 14px;
  text-decoration: none;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const ExternalIcon = styled.span`
  margin-left: 4px;
  font-size: 12px;
`;

const Chip = styled.div`
  margin-top: 8px;
  padding: 9px 10px;
  border-radius: 6px;
  background: #f5f6f8;
  font-size: 13px;
  font-weight: 600;
  color: #0a1f44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Representation = styled.div`
  margin-top: 10px;
  font-size: 13px;
  color: #53627c;
`;

const RepresentationLink = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0584d7;
  font-size: 14px;
  padding: 10px 0;
  margin-bottom: 8px;
  cursor: pointer;
  min-height: 44px;
`;

const Note = styled.div`
  margin-top: 8px;
  padding: 9px 10px;
  border-radius: 6px;
  background: #f5f6f8;
  font-size: 13px;
  font-style: italic;
  color: #0a1f44;
  line-height: 19px;
  overflow-wrap: anywhere;
`;

const NoteEmpty = styled.div`
  margin-top: 8px;
  padding: 9px 10px;
  border-radius: 6px;
  background: #f5f6f8;
  font-size: 13px;
  font-style: italic;
  color: #8a94a6;
  line-height: 19px;
`;

const Skeleton: any = styled.div`
  height: ${(p: any) => p.height || 16}px;
  width: ${(p: any) => (p.width ? `${p.width}px` : "100%")};
  max-width: 100%;
  border-radius: 6px;
  background: #eef1f5;
  margin-bottom: 8px;
`;

// Informational, not a warning (the amber LateNotice is the deadline): light blue, bold.
const CancelledNotice = styled.div`
  margin: 4px 0 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #eef6fe;
  border: 1px solid #b9dcfa;
  color: #0a4f8a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  text-align: center;
`;

const LateNotice = styled.div`
  margin: 4px 0 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fff4e5;
  border: 1px solid #efc58e;
  color: #5c3000;
  font-size: 13px;
  line-height: 1.45;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

// Outlined counterpart to PrimaryButton (as on step 2): the second of two real actions.
const SecondaryButton = styled.button`
  width: 100%;
  ${membershipSecondary}
  padding: 13px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 14px 0;
  cursor: pointer;
  min-height: 44px;

  &:disabled {
    color: #8a94a6;
    cursor: default;
  }
`;

const SheetText = styled.p`
  font-size: 14px;
  color: #53627c;
  line-height: 1.5;
  margin: 0 0 4px;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #53627c;
  text-align: center;
  margin-top: 4px;
  line-height: 18px;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #c7053f;
  text-align: center;
  margin-bottom: 10px;
`;

const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 0 8px;
`;

const Spinner: any = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #e3e8ee;
  border-top-color: ${(p: any) => (p.stopped ? "#e3e8ee" : "#0f9bf0")};
  margin-bottom: 14px;
  animation: ${(p: any) => (p.stopped ? "none" : "sfReviewSpin 0.9s linear infinite")};

  @keyframes sfReviewSpin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Who asked for less motion still sees that something is happening: the ring
     stays, only the spinning stops. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const WaitingMessage = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #122640;
`;

const WaitingSub = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-top: 8px;
  line-height: 20px;
`;

const WaitingButton = styled(PrimaryButton)`
  margin-top: 18px;
`;

interface StateProps {
  breakdownId: string | null;
  roleId: string | null;
  sessionId: string | null;
  draft: iRootState["submissionSignupModel"]["draft"];
  review: iRootState["submissionStep4Model"]["review"];
  submissionInfo: iRootState["submissionFlowModel"]["submissionInfo"];
  membership: iRootState["submissionStep4Model"]["membership"];
  loading: boolean;
  error: iRootState["submissionStep4Model"]["error"];
  signupErrorCode: string | null;
}

interface DispatchProps {
  ensureDraft: (payload: {
    breakdownId: string | null;
    roleId: string | null;
  }) => Promise<any>;
  loadReview: (submissionId: number) => Promise<any>;
  saveRepresentation: (payload: {
    submissionId: number;
    request: ISetRepresentationRequest;
  }) => Promise<{ representation: IRepresentationDto | null; error: string | null }>;
  setJobAlerts: (subscribe: boolean) => void;
  sendSubmission: (submissionId: number) => Promise<any>;
  sendAfterPayment: (
    submissionId: number
  ) => Promise<{ sent: ISendSubmissionResponse | null; error: string | null }>;
  holdSubmission: (payload: { submissionId: number; request: any }) => Promise<any>;
  recordExposure: (payload: any) => Promise<Step4Variant>;
  startMembershipCheckout: (payload: {
    submissionId: number;
    search: string;
  }) => Promise<{ url: string | null; blocked: string | null }>;
  startDirectCheckout: (payload: {
    submissionId: number;
    search: string;
    variant: Step4Variant;
  }) => Promise<{ url: string | null; blocked: string | null; direct: boolean }>;
  waitForPlan: () => Promise<ISubmissionMembershipDto | null>;
  refreshGate: (payload: { history: any; search?: string; paid?: boolean }) => Promise<any>;
  setStep4Error: (error: { code: string; message: string } | null) => void;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  roleId: state.submissionFlowModel.roleId,
  sessionId: state.submissionSignupModel.sessionId,
  draft: state.submissionSignupModel.draft,
  review: state.submissionStep4Model.review,
  submissionInfo: state.submissionFlowModel.submissionInfo,
  membership: state.submissionStep4Model.membership,
  loading: state.submissionStep4Model.loading,
  error: state.submissionStep4Model.error,
  signupErrorCode: state.submissionSignupModel.errorCode,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
  loadReview: dispatch.submissionStep4Model.loadReview,
  saveRepresentation: dispatch.submissionStep4Model.saveRepresentation,
  setJobAlerts: dispatch.submissionStep4Model.setJobAlerts,
  sendSubmission: dispatch.submissionStep4Model.sendSubmission,
  sendAfterPayment: dispatch.submissionStep4Model.sendAfterPayment,
  holdSubmission: dispatch.submissionStep4Model.holdSubmission,
  recordExposure: dispatch.submissionStep4Model.recordExposure,
  startMembershipCheckout: dispatch.submissionStep4Model.startMembershipCheckout,
  startDirectCheckout: dispatch.submissionStep4Model.startDirectCheckout,
  waitForPlan: dispatch.submissionStep4Model.waitForPlan,
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  setStep4Error: dispatch.submissionStep4Model.setError,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionReviewPage);
